import React, { useState, useEffect, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie"; // ✅ Cookie-Management
import { FaFileExcel, FaDatabase, FaEdit, FaUpload, FaTimes, FaCheckCircle, FaExclamationTriangle, FaSpinner, FaChevronDown, FaChevronRight, FaFileAlt, FaInfoCircle, FaPlus, FaSync, FaHome, FaLink, FaFolderOpen, FaExchangeAlt, FaMoneyBillWave, FaFileContract, FaChartLine, FaCamera, FaMapMarkerAlt, FaCar, FaClipboardList, FaBuilding, FaCity, FaCompress, FaSearchPlus, FaStar, FaFileArchive, FaTachometerAlt, FaImage, FaFileInvoiceDollar, FaShieldAlt, FaUsers } from "react-icons/fa";
import "./HomePage.css";
import { APP_SECTIONS, iconByKey } from "../appRegistry";
import { WELLE38_UI } from "../featureFlags";
import FavoritenRail, { FAV_MIME } from "../components/home/FavoritenRail";
import AutomatikBand from "../components/home/AutomatikBand";
import TodoRail from "../components/home/TodoRail";
import ExcelBatchPage from "./ExcelBatchPage";
import Toast from "../components/Toast";
import AddEntryModal, { ERROR_TABLE_MAP } from "../components/AddEntryModal";
import { API_BASE } from "../api/config";
import { useAuth } from "../context/AuthContext";

const API = `${API_BASE}/rename-me-ocr/api`;

const API_BASE_HOME = `${API_BASE}/rename-me-ocr/api`;

// Kachel-Namen: weiche Trennstellen (\u00AD) in langen Wörtern — die Kacheln sind schmal,
// und der Browser trennt Deutsch nicht zuverlässig selbst („Verschieb-en" statt „Ver-schieben").
const TRENNSTELLEN = {
  Verschieben: "Ver\u00ADschieben", Zählerstände: "Zähler\u00ADstände", verarbeiten: "ver\u00ADarbeiten",
  Miethistorie: "Miet\u00ADhistorie", Dokumente: "Doku\u00ADmente", "(Kündigungen)": "(Kündi\u00ADgungen)",
  Arbeitsnachweis: "Arbeits\u00ADnachweis", "Gemini-Regelwerk": "Gemini-Regel\u00ADwerk",
};
const trennbar = (text) => String(text || "").split(" ").map((w) => TRENNSTELLEN[w] || w).join(" ");

const HomePage = ({ setIsAuthenticated: _legacySetAuth }) => {
  const navigate = useNavigate();
  const { user, login: authLogin, authReady } = useAuth();
  const isAuthenticated = !!user;
  // Prüft ob ein Modul freigeschaltet ist. Bei leerer module-Liste: alles erlaubt (Fallback)
  const hasModule = (key) => {
    if (!user || !user.module || user.module.length === 0) return true;
    return user.module.includes(key);
  };
  const [showChangelog, setShowChangelog] = useState(false);
  const [dataPathPickerOpen, setDataPathPickerOpen] = useState(false);
  const [releaseInfo, setReleaseInfo] = useState({ version: "", title: "", description: "", date: "" });
  const [kuerzel, setKuerzel] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [runningBatch, setRunningBatch] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [batchError, setBatchError] = useState("");

  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showExcelBatch, setShowExcelBatch] = useState(false); // Excel Batch Dropbox Modal
  const [liveFeed, setLiveFeed] = useState([]);
  const [folderStats, setFolderStats] = useState({ inbox: null, outbox: null, processed: null, error: null });
  const [progressPct, setProgressPct] = useState(0);
  const [batchDone, setBatchDone] = useState(false);

  // Release Info laden
  useEffect(() => {
    const publicUrl = process.env.PUBLIC_URL || '';
    fetch(`${publicUrl}/RELEASE.json`)
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(data => {
        console.log('[RELEASE] Loaded:', data);
        setReleaseInfo(data);
      })
      .catch((err) => {
        console.warn('[RELEASE] Could not load:', err);
        setReleaseInfo({ version: "3.1.6", title: "Batch Review", description: "", date: "2025-12-28" });
      });
  }, []);

  // === BATCH REVIEW STATE ===
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewBatchId, setReviewBatchId] = useState(null);
  const [reviewFiles, setReviewFiles] = useState([]);
  const [reviewErrorFiles, setReviewErrorFiles] = useState([]);  // Haben noch Fehler
  const [reviewWarningFiles, setReviewWarningFiles] = useState([]); // Haben # im Namen (Warnung)
  const [reviewEditedFiles, setReviewEditedFiles] = useState([]); // Bearbeitet, bereit zum Verschieben
  const [reviewSuccessFiles, setReviewSuccessFiles] = useState([]); // Schon im Outbox
  const [reviewSelectedFileId, setReviewSelectedFileId] = useState(null);
  const [reviewSelectedFile, setReviewSelectedFile] = useState(null);
  const [reviewPreviewUrl, setReviewPreviewUrl] = useState(null);
  const [reviewShowSuccess, setReviewShowSuccess] = useState(true);
  const [reviewShowEdited, setReviewShowEdited] = useState(true);
  const [reviewEditingId, setReviewEditingId] = useState(null);
  const [reviewEditValue, setReviewEditValue] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewApplying, setReviewApplying] = useState(false);
  const [reviewApplyResult, setReviewApplyResult] = useState(null);
  const reviewInputRef = useRef(null);
  const reviewEditCancelledRef = useRef(false);
  const feedListRef = useRef(null);
  const [reviewManuallyEditedIds, setReviewManuallyEditedIds] = useState(new Set());
  const [reviewCollapsed, setReviewCollapsed] = useState({ fehler: false, warnung: false });
  const toggleReviewSection = (key) => setReviewCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  // === ADD ENTRY MODAL STATE ===
  const [addEntryModalOpen, setAddEntryModalOpen] = useState(false);
  const [addEntryTarget, setAddEntryTarget] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [pendingReprocessIds, setPendingReprocessIds] = useState(new Set());
  const [reprocessing, setReprocessing] = useState(false);

  useEffect(() => {
    // Rückwärtskompatibilität: sessionId aus Cookie in localStorage
    const sessionId = Cookies.get("sessionId");
    const username = Cookies.get("username");
    if (sessionId && username) {
      localStorage.setItem("sessionId", sessionId);
      localStorage.setItem("username", username);
    }
  }, []);

  // Pfeiltasten-Navigation für Review
  useEffect(() => {
    if (!reviewMode || reviewLoading) return;
    
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT') return; // Nicht beim Editieren
      if (reviewEditingId) return; // Nicht während Dateinamen-Bearbeitung
      
      // Alle sichtbaren Dateien in Reihenfolge sammeln
      const allFiles = [
        ...reviewErrorFiles,
        ...reviewWarningFiles,
        ...reviewEditedFiles,
        ...(reviewShowSuccess ? reviewSuccessFiles : [])
      ];
      if (allFiles.length === 0) return;
      
      const currentIndex = allFiles.findIndex(f => f.id === reviewSelectedFileId);
      
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        const nextIndex = currentIndex < allFiles.length - 1 ? currentIndex + 1 : 0;
        const nextFile = allFiles[nextIndex];
        setReviewSelectedFileId(nextFile.id);
        setReviewSelectedFile(nextFile);
        setReviewPreviewUrl(`${API_BASE_HOME}/batch/dropbox/file-preview/${reviewBatchId}/${nextFile.id}`);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : allFiles.length - 1;
        const prevFile = allFiles[prevIndex];
        setReviewSelectedFileId(prevFile.id);
        setReviewSelectedFile(prevFile);
        setReviewPreviewUrl(`${API_BASE_HOME}/batch/dropbox/file-preview/${reviewBatchId}/${prevFile.id}`);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [reviewMode, reviewLoading, reviewEditingId, reviewErrorFiles, reviewWarningFiles, reviewEditedFiles, reviewSuccessFiles, reviewShowSuccess, reviewSelectedFileId, reviewBatchId]);

  // Event-Listener für Review-Modal aus Navbar
  const [reviewTrigger, setReviewTrigger] = useState(0);
  
  useEffect(() => {
    const handleOpenReview = () => {
      setShowBatchModal(true);
      setReviewTrigger(t => t + 1); // Trigger erhöhen um useEffect auszulösen
    };
    window.addEventListener('openBatchReview', handleOpenReview);
    return () => window.removeEventListener('openBatchReview', handleOpenReview);
  }, []);

  // Wenn reviewTrigger sich ändert, starte Review
  useEffect(() => {
    if (reviewTrigger > 0) {
      // Review laden
      setReviewMode(true);
      setReviewLoading(true);
      setReviewApplyResult(null);
      
      const loadReview = async () => {
        try {
          const res = await fetch(`${API_BASE_HOME}/batch/dropbox/preview?limit=100&source=output`, {
            method: "POST",
          });
          
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          
          const data = await res.json();
          console.log("[REVIEW] Loaded files:", data);
          
          setReviewBatchId(data.batch_id);
          setReviewFiles(data.files || []);
          
          const allFiles = data.files || [];
          const isFromError = (f) => f.source_folder?.toLowerCase().includes('error') || f.original_route === 'ERROR';
          const hasWarning = (f) => f.proposed_name?.includes('#');
          allFiles.forEach(f => { if (!f.original_route) f.original_route = f.route; });
          
          const errors = allFiles.filter(f => f.has_error);
          const warnings = allFiles.filter(f => !f.has_error && hasWarning(f));
          const edited = allFiles.filter(f => !f.has_error && !hasWarning(f) && isFromError(f));
          const success = allFiles.filter(f => !f.has_error && !hasWarning(f) && !isFromError(f));
          
          setReviewErrorFiles(errors);
          setReviewWarningFiles(warnings);
          setReviewEditedFiles(edited);
          setReviewSuccessFiles(success);
          
          const firstFile = errors[0] || warnings[0] || edited[0] || success[0];
          if (firstFile) {
            setReviewSelectedFileId(firstFile.id);
            setReviewSelectedFile(firstFile);
            setReviewPreviewUrl(`${API_BASE_HOME}/batch/dropbox/file-preview/${data.batch_id}/${firstFile.id}`);
          }
        } catch (e) {
          console.error("[REVIEW] Error:", e);
          setBatchError(`Review laden fehlgeschlagen: ${e.message}`);
        } finally {
          setReviewLoading(false);
        }
      };
      
      loadReview();
    }
  }, [reviewTrigger]);

  const handleLogin = async () => {
    if (!kuerzel.trim() || !password) {
      setError("Bitte Kürzel und Passwort eingeben");
      return;
    }
    setLoginLoading(true);
    setError("");
    try {
      const me = await authLogin(kuerzel.trim(), password);
      setPassword("");
      if (me?.zugang_typ === "nur_zeiterfassung") {
        navigate("/arbeitsnachweis");
        return;
      }
    } catch (err) {
      if (err.redirect) {
        navigate(err.redirect);
        return;
      }
      setError(err.message || "Login fehlgeschlagen");
    } finally {
      setLoginLoading(false);
    }
  };

  // Auto-Scroll Feed-Liste zum letzten Eintrag
  useEffect(() => {
    if (feedListRef.current) {
      feedListRef.current.scrollTop = feedListRef.current.scrollHeight;
    }
  }, [liveFeed]);

  if (!authReady) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ color: 'var(--color-gray-500)' }}>Lade…</span>
    </div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Anmeldung</h2>

          <label>Kürzel:</label>
          <input
            type="text"
            className="auth-input-field"
            style={{
              display: "block",
              width: "100%",
              padding: "10px 14px",
              fontFamily: "inherit",
              fontSize: "1rem",
              color: "#f1f5f9",
              background: "rgba(30, 41, 59, 0.8)",
              border: "2px solid #475569",
              borderRadius: "8px",
              marginBottom: "16px",
              boxSizing: "border-box",
              WebkitAppearance: "none",
              appearance: "none",
            }}
            value={kuerzel}
            onChange={(e) => setKuerzel(e.target.value.toUpperCase())}
            placeholder="z. B. LBO"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          />

          <label>Passwort:</label>
          <input
            type="password"
            className="auth-input-field"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Passwort eingeben"
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          />

          <button className="auth-button" onClick={handleLogin} disabled={loginLoading}>
            {loginLoading ? "Anmelden…" : "Anmelden"}
          </button>
          {error && <p className="error">{error}</p>}
        </div>
      </div>
    );
  }

  const createSession = async () => {
    try {
      const response = await fetch(`${API_BASE}/session/start`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Fehler beim Erstellen der Sitzung");
      }

      const data = await response.json();
      localStorage.setItem("sessionId", data.session_id);
      console.log("Sitzung erstellt:", data.session_id);

      navigate("/all-in-one");
    } catch (error) {
      console.error("Fehler beim Erstellen der Sitzung:", error);
      alert("Es gab ein Problem beim Starten der Sitzung. Bitte versuchen Sie es erneut.");
    }
  };

  const startDatabaseSession = async () => {
    try {
      const response = await fetch(`${API_BASE}/session/start`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Fehler beim Starten der Datenbank-Sitzung");
      }

      const data = await response.json();
      localStorage.setItem("sessionId", data.session_id);
      console.log("Datenbank-Sitzung gestartet:", data.session_id);

      navigate("/database-import");
    } catch (error) {
      console.error("Fehler beim Starten der Datenbank-Sitzung:", error);
      alert("Fehler beim Starten der Datenbank-Sitzung. Bitte erneut versuchen.");
    }
  };

  const fetchBatchStats = async () => {
    try {
      const resp = await fetch(`${API}/dropbox/stats`);
      if (!resp.ok) return;
      const j = await resp.json();
      const inbox = j.inbox_count ?? j.inbox ?? null;
      const outbox = j.outbox_count ?? j.outbox ?? null;
      const processed = j.processed_count ?? j.processed ?? null;
      const error = j.error_count ?? j.errors ?? j.error ?? null;
      setFolderStats({ inbox, outbox, processed, error });
    } catch (_) {}
  };

  // Helper: fetchBatchStatsOnce returns stats object (for batch logic)
  const fetchBatchStatsOnce = async () => {
    try {
      const resp = await fetch(`${API}/dropbox/stats`);
      if (!resp.ok) return null;
      const j = await resp.json();
      const inbox = j.inbox_count ?? j.inbox ?? null;
      const outbox = j.outbox_count ?? j.outbox ?? null;
      const processed = j.processed_count ?? j.processed ?? null;
      const error = j.error_count ?? j.errors ?? j.error ?? null;
      const obj = { inbox, outbox, processed, error };
      setFolderStats(obj);
      return obj;
    } catch (_) { return null; }
  };

  // -- Live-Feed helpers (spinner -> final status) --
  const pushSpinner = (label = "Verarbeite …") => {
    const now = Date.now();
    const item = {
      id: `${now}-${Math.random().toString(36).slice(2, 7)}`,
      text: label,
      status: "spinner",
      startedAt: now,
    };
    flushSync(() => setLiveFeed((prev) => [...prev, item].slice(-300)));
    return item.id;
  };

  // Spinner-Text aktualisieren ohne Status zu ändern (für Live-Updates während Verarbeitung)
  // KEIN flushSync hier – wird aus setInterval aufgerufen, React rendert im nächsten Frame
  const updateSpinner = (id, text) => {
    setLiveFeed((prev) =>
      prev.map((it) => (it.id === id && it.status === "spinner" ? { ...it, text } : it))
    );
  };

  const formatTime = (ms) => {
    try {
      return new Date(ms).toLocaleTimeString("de-DE", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const humanDuration = (ms) => {
    if (ms == null || Number.isNaN(ms)) return "";
    if (ms < 1000) return `${ms} ms`;
    const sec = ms / 1000;
    if (sec < 60) return `${sec.toFixed(1)} s`;
    const min = Math.floor(sec / 60);
    const rest = Math.round(sec % 60);
    return `${min} min ${rest}s`;
  };

  // Generischer Finalizer bleibt erhalten (für andere Stellen)
  const finalizeFeed = (id, text, status = "ok") => {
    setLiveFeed((prev) =>
      prev.map((it) => (it.id === id ? { ...it, text, status } : it))
    );
  };

  // Spezieller Finalizer für Batch-Dateien:
  // Speichert strukturierte Felder (ts, baseName, finalName, durLabel, message)
  const finalizeFileFeed = (id, { orig, finalName, status, message }) => {
    flushSync(() =>
      setLiveFeed((prev) =>
        prev.map((it) => {
          if (it.id !== id) return it;
          const now = Date.now();
          const startedAt = it.startedAt || now;
          const durationMs = now - startedAt;
          const ts = formatTime(startedAt);
          const baseName = orig || "(unbekannt)";
          const durLabel = humanDuration(durationMs);
          return { ...it, status, ts, baseName, finalName: finalName || null, durLabel, message: message || "" };
        })
      )
    );
  };

  const addFeed = (text, status = "ok") => {
    const id = pushSpinner("");
    finalizeFeed(id, text, status);
  };

  // Der eigentliche Batch-Loop – läuft AUSSERHALB des Click-Events
  // damit React das Modal zuerst committen kann
  const runBatchLoop = async () => {
    setRunningBatch(true);
    let poller = null;
    try {
      const sessionId = localStorage.getItem("sessionId") || Cookies.get("sessionId") || "";
      const url = `${API}/dropbox/run-once?limit=1`;

      let noWorkRounds = 0;
      let rounds = 0;
      let stop = false;

      // Stats poller while running
      poller = setInterval(() => { fetchBatchStats(); }, 2000);

      while (!stop && rounds < 50) { // safety cap
        rounds += 1;
        const spinnerId = pushSpinner("Prüfe Inbox…");
        let data = null;

        // Pollt /dropbox/processing-now während der HTTP-Call läuft
        // → zeigt Dateiname + Phase (OCR / KI) im Spinner an
        let seenFile = false;
        const livePoller = setInterval(async () => {
          try {
            const sr = await fetch(`${API}/dropbox/processing-now`);
            if (!sr.ok) return;
            const ns = await sr.json();
            if (!ns.file && !ns.phase) return;
            const shortName = (ns.file || "")
              .replace(/\.pdf$/i, "")
              .split(" - ").slice(0, 3).join(" - ");
            let phaseLabel;
            if (ns.phase === "ki") {
              seenFile = true;
              const fields = (ns.ki_fields || []).join(", ");
              phaseLabel = `⏳ KI läuft… (${fields}) – kann 30–60 s dauern`;
            } else if (ns.phase === "ocr") {
              seenFile = true;
              phaseLabel = "🔍 OCR läuft…";
            } else if (ns.phase === "download") {
              seenFile = true;
              phaseLabel = "📥 Lade von Dropbox…";
            } else if (ns.phase === "done") {
              seenFile = true;
              phaseLabel = "✅ Fertig";
            } else {
              if (!seenFile) return;
              phaseLabel = "Verarbeite…";
            }
            updateSpinner(spinnerId, shortName ? `${shortName} — ${phaseLabel}` : phaseLabel);
          } catch (_) {}
        }, 300);

        try {
          const resp = await fetch(url, {
            method: "POST",
            headers: { "Accept": "application/json", "session-id": sessionId },
          });
          if (resp.ok) {
            data = await resp.json();
          }
        } catch (_) {}
        clearInterval(livePoller);
        if (!data) {
          throw new Error("Endpoint /rename-me-ocr/api/dropbox/run-once nicht erreichbar.");
        }
        // Welle 104.1: Gemini gesperrt → Batch läuft nicht (Backend bricht vorher ab)
        if (data.gesperrt) {
          throw new Error(`${data.gesperrt} — Batch nicht ausgeführt.`);
        }

        // Update UI
        setBatchResult(data);
        const processed = Number(data.processed ?? 0);
        const errors = Number(data.errors ?? 0);
        const trace = Array.isArray(data.trace) ? data.trace : [];

        // Parse backend trace für eine Datei und Live-Feed-Zeile bauen
        const okLine = trace.find((t) => /^✔️ OUTBOX: /.test(String(t)));
        const errLine = trace.find((t) => /^❌ ERROR: /.test(String(t)));
        let orig = null;
        let finalName = null;
        let message = "";
        let status = "ok";

        if (okLine) {
          const m = okLine.match(/^✔️ OUTBOX: (.+?) \(orig: (.+?)\)/);
          if (m) { finalName = m[1]; orig = m[2]; }
          status = "ok";
        } else if (errLine) {
          const m = errLine.match(/^❌ ERROR: (.+?) \(orig: (.+?)\)\s+—\s+Gründe: (.+)$/);
          if (m) { finalName = m[1]; orig = m[2]; message = m[3]; }
          else { message = String(errLine); }
          status = "error";
        } else {
          const chk = trace.find((t) => /^\[check\]\s+/.test(String(t)));
          if (chk) { const mChk = String(chk).match(/^\[check\]\s+(.+)$/); if (mChk) orig = mChk[1]; }
          status = processed > 0 ? "ok" : errors > 0 ? "error" : "ok";
          if (!orig) message = processed > 0 ? "verarbeitet" : errors > 0 ? "Fehler" : "";
        }

        finalizeFileFeed(spinnerId, { orig, finalName, status, message });

        // Fortschritt anhand Inbox-Zähler abschätzen
        const statsNow = await fetchBatchStatsOnce();
        const inboxNow = statsNow ? (statsNow.inbox ?? null) : null;

        if (processed === 0) {
          noWorkRounds += 1;
        } else {
          noWorkRounds = 0;
        }
        if (noWorkRounds >= 2 && inboxNow === 0) {
          stop = true;
        }
        setProgressPct((p) => Math.min(98, processed > 0 ? p + 6 : p + 1));
      }

      clearInterval(poller);
      poller = null;
      await fetchBatchStats();
      setProgressPct(100);
      setBatchDone(true);

      // === NACH BATCH: Prüfen ob Fehler-Dateien vorhanden ===
      const finalStats = await fetchBatchStatsOnce();
      if (finalStats && finalStats.error > 0) {
        setTimeout(() => { startReviewMode(); }, 1000);
      }
    } catch (e) {
      setBatchError(e.message || String(e));
    } finally {
      if (poller) clearInterval(poller);
      setRunningBatch(false);
    }
  };

  // Button-Handler: nur Modal öffnen + Reset, dann Loop per setTimeout starten
  // → setTimeout(0) stellt sicher dass React das Modal zuerst committet (DOM mounted)
  // bevor der erste pushSpinner läuft
  const handleBatchRename = () => {
    setBatchError("");
    setBatchResult(null);
    setProgressPct(5);
    setBatchDone(false);
    setFolderStats({ inbox: null, outbox: null, processed: null, error: null });
    setLiveFeed([]);
    setShowBatchModal(true);
    // Zuerst Stats laden, dann Loop starten – gibt React Zeit das Modal zu rendern
    fetchBatchStats().finally(() => {
      setTimeout(() => runBatchLoop(), 0);
    });
  };

  const closeBatchModal = () => {
    if (runningBatch) return; // nicht während Lauf schließen
    setShowBatchModal(false);
    // Review-State zurücksetzen
    setReviewMode(false);
    setReviewFiles([]);
    setReviewErrorFiles([]);
    setReviewSuccessFiles([]);
    setReviewBatchId(null);
    setReviewApplyResult(null);
  };

  // === REVIEW FUNKTIONEN ===
  const startReviewMode = async () => {
    setReviewMode(true);
    setReviewLoading(true);
    setReviewApplyResult(null);
    
    try {
      // source=output lädt aus Outbox+Error Ordnern (nach dem Batch)
      const res = await fetch(`${API_BASE_HOME}/batch/dropbox/preview?limit=100&source=output`, {
        method: "POST",
      });
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      
      const data = await res.json();
      console.log("[REVIEW] Loaded files:", data);
      
      setReviewBatchId(data.batch_id);
      setReviewFiles(data.files || []);
      
      // Kategorisieren:
      // - errors: haben noch Fehler
      // - warnings: haben # im Namen (Warnung)
      // - edited: aus Error-Ordner, jetzt OK (bereit zum Verschieben)
      // - success: aus Outbox (schon richtig)
      const allFiles = data.files || [];
      
      // Helper: Check if file is from error folder (via source_folder path OR original route)
      const isFromError = (f) => f.source_folder?.toLowerCase().includes('error') || f.original_route === 'ERROR';
      
      // Helper: Check if file has # in proposed_name (warning)
      const hasWarning = (f) => f.proposed_name?.includes('#');
      
      // Mark original route for later reference
      allFiles.forEach(f => { if (!f.original_route) f.original_route = f.route; });
      
      const errors = allFiles.filter(f => f.has_error);
      const warnings = allFiles.filter(f => !f.has_error && hasWarning(f));
      const edited = allFiles.filter(f => !f.has_error && !hasWarning(f) && isFromError(f));
      const success = allFiles.filter(f => !f.has_error && !hasWarning(f) && !isFromError(f));
      
      console.log('[REVIEW] Categories:', { errors: errors.length, warnings: warnings.length, edited: edited.length, success: success.length });
      console.log('[REVIEW] Sample file:', allFiles[0]);
      
      setReviewErrorFiles(errors);
      setReviewWarningFiles(warnings);
      setReviewEditedFiles(edited);
      setReviewSuccessFiles(success);
      
      // Erste Fehler-Datei auswählen, dann edited, dann success
      const firstFile = errors[0] || edited[0] || success[0];
      if (firstFile) {
        setReviewSelectedFileId(firstFile.id);
        setReviewSelectedFile(firstFile);
        loadReviewPreview(data.batch_id, firstFile.id);
      }
      
    } catch (e) {
      console.error("[REVIEW] Error:", e);
      setBatchError(`Review laden fehlgeschlagen: ${e.message}`);
    } finally {
      setReviewLoading(false);
    }
  };

  const loadReviewPreview = (batchId, fileId) => {
    if (!batchId || !fileId) return;
    setReviewPreviewUrl(`${API_BASE_HOME}/batch/dropbox/file-preview/${batchId}/${fileId}`);
  };

  const handleReviewFileSelect = (file) => {
    setReviewSelectedFileId(file.id);
    setReviewSelectedFile(file);
    loadReviewPreview(reviewBatchId, file.id);
  };

  const startReviewEdit = (fileId, currentName) => {
    setReviewEditingId(fileId);
    setReviewEditValue(currentName.replace(/\.pdf$/i, ""));
    setTimeout(() => reviewInputRef.current?.focus(), 0);
  };

  const stopReviewEdit = () => {
    setReviewEditingId(null);
    setReviewEditValue("");
  };

  const handleReviewNameChange = (newValue) => {
    setReviewEditValue(newValue);
  };

  const saveReviewFilename = async (fileId, newName) => {
    if (!reviewBatchId || !fileId || !newName.trim()) {
      console.log("[SAVE] Aborted - missing data:", { reviewBatchId, fileId, newName });
      return;
    }
    
    console.log("[SAVE] Saving filename:", { fileId, newName, batchId: reviewBatchId });
    
    setReviewSaving(true);
    try {
      const url = `${API_BASE_HOME}/batch/dropbox/update-filename?batch_id=${reviewBatchId}&file_id=${fileId}&new_name=${encodeURIComponent(newName)}`;
      console.log("[SAVE] URL:", url);
      
      const res = await fetch(url, { method: "POST" });
      
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Speichern fehlgeschlagen");
      }
      
      const data = await res.json();
      console.log("[SAVE] Response:", data);
      
      // State komplett neu berechnen - als "edited" markieren wenn aus Error-Ordner
      const originalFile = reviewFiles.find(f => f.id === fileId);
      console.log("[SAVE] Original file:", originalFile);
      console.log("[SAVE] source_folder:", originalFile?.source_folder);
      console.log("[SAVE] original_route:", originalFile?.original_route);
      
      // Prüfe ob aus Error-Ordner (via source_folder ODER original_route)
      const isFromErrorFolder = originalFile?.source_folder?.toLowerCase().includes('error') || originalFile?.original_route === 'ERROR';
      console.log("[SAVE] isFromErrorFolder:", isFromErrorFolder);
      
      const updatedFile = {
        ...originalFile,
        proposed_name: data.new_name,
        has_error: data.has_error,
        route: data.route,
        // original_route beibehalten!
        original_route: originalFile?.original_route || originalFile?.route,
        manually_edited: true,  // Immer als manuell bearbeitet markieren
        edited: true
      };
      
      // Alle Files aktualisieren
      const newFiles = reviewFiles.map(f => f.id === fileId ? updatedFile : f);
      setReviewFiles(newFiles);
      
      // Listen neu filtern:
      // - errorFiles: haben noch Fehler
      // - warningFiles: haben # im Namen (Warnung) - MUSS VOR edited/success geprüft werden!
      // - editedFiles: manuell bearbeitet ODER aus Error-Ordner, jetzt OK, noch nicht verschoben
      // - successFiles: aus Outbox, nicht bearbeitet
      const isFromError = (f) => f.source_folder?.toLowerCase().includes('error') || f.original_route === 'ERROR';
      const hasWarning = (f) => f.proposed_name?.includes('#');
      
      const errors = newFiles.filter(f => f.has_error);
      const warnings = newFiles.filter(f => !f.has_error && hasWarning(f));
      const edited = newFiles.filter(f => !f.has_error && !hasWarning(f) && (f.manually_edited || isFromError(f)) && !f.moved);
      const success = newFiles.filter(f => !f.has_error && !hasWarning(f) && !f.manually_edited && !isFromError(f));
      
      console.log("[SAVE] Categorized - errors:", errors.length, "warnings:", warnings.length, "edited:", edited.length, "success:", success.length);
      console.log("[SAVE] Edited files:", edited.map(f => ({ id: f.id, name: f.proposed_name, source: f.source_folder })));
      
      setReviewErrorFiles(errors);
      setReviewWarningFiles(warnings);
      setReviewEditedFiles(edited);
      setReviewSuccessFiles(success);
      
      // Als manuell bearbeitet markieren (für exclude_ids)
      setReviewManuallyEditedIds(prev => new Set([...prev, fileId]));
      
      // Selected file aktualisieren
      if (reviewSelectedFileId === fileId) {
        setReviewSelectedFile(updatedFile);
      }
      
      console.log("[SAVE] Updated - has_error:", data.has_error, "edited:", updatedFile.edited);
      
    } catch (e) {
      setBatchError(`Speichern fehlgeschlagen: ${e.message}`);
    } finally {
      setReviewSaving(false);
    }
  };

  const handleReviewApplyAll = async () => {
    if (!reviewBatchId) return;
    
    setReviewApplying(true);
    
    try {
      // Nur fehlerhafte (bearbeitete) Dateien verschieben (errors_only=true)
      const excludeParam = reviewManuallyEditedIds.size > 0
        ? `&exclude_ids=${[...reviewManuallyEditedIds].join(",")}`
        : "";
      const res = await fetch(`${API_BASE_HOME}/batch/dropbox/apply-all?batch_id=${reviewBatchId}&errors_only=true${excludeParam}`, {
        method: "POST",
      });
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      
      const data = await res.json();
      setReviewApplyResult(data);
      
      if (data.ok) {
        // Bearbeitete Dateien wurden verschoben - in Success verschieben
        const movedFiles = reviewEditedFiles.map(f => ({ ...f, moved: true }));
        setReviewSuccessFiles([...reviewSuccessFiles, ...movedFiles]);
        setReviewEditedFiles([]);
        
        // reviewFiles auch aktualisieren
        const newFiles = reviewFiles.map(f => {
          const wasEdited = reviewEditedFiles.find(e => e.id === f.id);
          return wasEdited ? { ...f, moved: true } : f;
        });
        setReviewFiles(newFiles);
        
        // Stats aktualisieren
        await fetchBatchStats();
      }
      
    } catch (e) {
      setBatchError(`Verschieben fehlgeschlagen: ${e.message}`);
    } finally {
      setReviewApplying(false);
    }
  };

  // === ADD ENTRY MODAL HANDLERS ===
  const openAddEntryModal = (file) => {
    const errorTags = file.error_tags || file.warnings || [];
    const firstErrorTag = errorTags.find(t => ERROR_TABLE_MAP[t]) || errorTags[0] || "missing_sender";
    const tableInfo = ERROR_TABLE_MAP[firstErrorTag] || { table: "absender", label: "Absender" };
    
    setAddEntryTarget({
      fileId: file.id,
      errorTag: firstErrorTag,
      defaultTable: tableInfo.table,
      prefillData: {
        sender_candidates: file.sender_candidates || [],
        prefix_candidates: file.prefix_candidates || [],
        date_candidates: file.date_candidates || [],
        doc_schema: file.schema || file.doc_schema || "",
      },
    });
    setAddEntryModalOpen(true);
  };

  const handleEntrySaved = (table, data) => {
    // Markiere Datei als "pending reprocess"
    if (addEntryTarget?.fileId) {
      setPendingReprocessIds(prev => new Set([...prev, addEntryTarget.fileId]));
    }
    
    // Toast anzeigen
    const tableLabels = {
      absender: "Absender",
      empfaenger_person: "Empfänger",
      empfaenger_objekt: "Empfänger",
      empfaenger_vertrag: "Empfänger",
      schema_keywords: "Schema-Keyword",
      special_rules: "Sonderregel",
    };
    setToast({
      show: true,
      message: `✅ ${tableLabels[table] || "Eintrag"} "${data.bezeichnung || data.keyword || data.anchor_token}" gespeichert`,
      type: "success",
    });
  };

  // === REPROCESS PENDING FILES ===
  const handleReprocessPending = async () => {
    if (pendingReprocessIds.size === 0 || !reviewBatchId) return;
    
    setReprocessing(true);
    
    try {
      // Rufe neues Reprocess-Endpoint auf mit den spezifischen File-IDs
      const fileIdsStr = [...pendingReprocessIds].join(",");
      const res = await fetch(`${API_BASE_HOME}/batch/dropbox/reprocess?batch_id=${reviewBatchId}&file_ids=${fileIdsStr}`, {
        method: "POST",
      });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      const data = await res.json();
      console.log("[REPROCESS] Result:", data);
      
      // Aktualisiere die betroffenen Dateien im State
      const updatedFilesMap = {};
      (data.files || []).forEach(f => { updatedFilesMap[f.id] = f; });
      
      // Aktualisiere reviewFiles
      const newReviewFiles = reviewFiles.map(f => updatedFilesMap[f.id] || f);
      setReviewFiles(newReviewFiles);
      
      // Kategorien neu berechnen
      const isFromError = (f) => f.source_folder?.toLowerCase()?.includes("/error");
      const hasHashInName = (f) => f.proposed_name?.includes("#") && !f.has_error;
      
      const errors = newReviewFiles.filter(f => f.has_error);
      const warnings = newReviewFiles.filter(f => hasHashInName(f));
      const edited = newReviewFiles.filter(f => !f.has_error && !hasHashInName(f) && isFromError(f));
      const success = newReviewFiles.filter(f => !f.has_error && !hasHashInName(f) && !isFromError(f));
      
      setReviewErrorFiles(errors);
      setReviewWarningFiles(warnings);
      setReviewEditedFiles(edited);
      setReviewSuccessFiles(success);
      setPendingReprocessIds(new Set());
      
      // Toast mit Ergebnis
      const fixedCount = (data.files || []).filter(f => !f.has_error).length;
      setToast({
        show: true,
        message: fixedCount > 0 
          ? `✅ ${fixedCount} Datei(en) jetzt OK!` 
          : `${data.reprocessed_count} Datei(en) neu verarbeitet`,
        type: fixedCount > 0 ? "success" : "info",
      });
      
      // Erste fehlerhafte Datei auswählen (falls vorhanden)
      const firstFile = errors[0] || warnings[0] || edited[0] || success[0];
      if (firstFile) {
        handleReviewFileSelect(firstFile);
      }
      
    } catch (e) {
      console.error("[REPROCESS] Error:", e);
      setToast({
        show: true,
        message: `Fehler: ${e.message}`,
        type: "error",
      });
    } finally {
      setReprocessing(false);
    }
  };

  // Registry-Aktionen (Kacheln ohne Route → Modals/Handler)
  const dispatchAction = (action) => {
    switch (action) {
      case "createSession": createSession(); break;
      case "startDatabaseSession": startDatabaseSession(); break;
      case "excelBatch": setShowExcelBatch(true); break;
      case "review": setShowBatchModal(true); setReviewTrigger((t) => t + 1); break;
      case "batch": handleBatchRename(); break;
      case "dataPathPicker": setDataPathPickerOpen(true); break;
      default: break;
    }
  };

  return (
    <div className="home">
  {/* Version Info Button - klein am Rand */}
  <div className={`version-info-toggle ${showChangelog ? 'expanded' : ''}`}>
    <button 
      className="version-toggle-btn"
      onClick={() => setShowChangelog(!showChangelog)}
      title={showChangelog ? "Schließen" : `Version ${releaseInfo.version}`}
    >
      <FaInfoCircle />
      {!showChangelog && <span className="version-badge">v{releaseInfo.version}</span>}
    </button>
    
    {showChangelog && (
      <div className="version-info-panel">
        <div className="version-header">
          <h3>🚀 Version {releaseInfo.version}</h3>
          <span className="version-date">{releaseInfo.date}</span>
        </div>
        <h4>{releaseInfo.title}</h4>
        <div className="version-description">
          {releaseInfo.description.split('\n').map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
      </div>
    )}
  </div>


      <h2>Willkommen bei ImHo Tools</h2>
      <p className="hp-subtitle">Wähle einen Bereich um zu starten</p>

      {WELLE38_UI ? (
      <div className="hp-shell">
        <FavoritenRail />
        <div className="hp-grid">
        {APP_SECTIONS.map((sec) => {
          if (!sec.modulKeys.some((k) => hasModule(k))) return null;
          const HeaderIcon = iconByKey(sec.headerIconKey);
          return (
            <div key={sec.key} className={`hp-section ${sec.sectionClass}`}>
              <div className={`hp-section-header ${sec.headerClass}`}>
                <div className="hp-section-icon-box"><HeaderIcon /></div>
                <h3 className="hp-section-title">{sec.title}</h3>
              </div>
              <div className="hp-items">
                {sec.items.map((item) => {
                  if (item.modulKey && !hasModule(item.modulKey)) return null;
                  if (item.adminOnly && user?.rolle !== "admin") return null;
                  const Icon = iconByKey(item.iconKey);
                  const isBatch = item.action === "batch";
                  const disabled = isBatch && runningBatch;
                  const onClick = item.route
                    ? () => navigate(item.route, item.navState ? { state: item.navState } : undefined)
                    : () => dispatchAction(item.action);
                  return (
                    <div
                      key={item.key}
                      className={`hp-item${isBatch ? " hp-batch-item" : ""}${disabled ? " hp-item-disabled" : ""}`}
                      draggable={!!item.route}
                      onDragStart={item.route ? (e) => {
                        e.dataTransfer.setData(FAV_MIME, JSON.stringify({
                          label: item.label, icon: item.iconKey, ziel: item.route, typ: "intern",
                        }));
                        e.dataTransfer.effectAllowed = "copy";
                      } : undefined}
                      onClick={!disabled ? onClick : undefined}
                      title={isBatch && runningBatch ? "Batch läuft bereits…" : item.label}
                    >
                      <div className="hp-item-icon-wrap"><Icon className={`hp-item-icon${isBatch && runningBatch ? " spin" : ""}`} /></div>
                      <div className="hp-item-text"><strong>{isBatch && runningBatch ? "Batch läuft…" : trennbar(item.label)}</strong></div>
                    </div>
                  );
                })}
              </div>
              {sec.key === "rename" && batchError && !showBatchModal && (
                <p className="hp-batch-error">{batchError}</p>
              )}
            </div>
          );
        })}
        </div>
        <div className="hp-rail-spalte">
          <TodoRail />
          <AutomatikBand />
        </div>
      </div>
      ) : (
      <div className="hp-grid">
        {/* ── Umsatz Import ─────────────────────────────── */}
        <div className="hp-section hp-sec-umsatz">
          <div className="hp-section-header hp-umsatz">
            <div className="hp-section-icon-box"><FaUpload /></div>
            <h3 className="hp-section-title">Umsatz Import</h3>
            <p className="hp-section-desc">CSV und Excel Import-Tools</p>
          </div>
          <div className="hp-items">
            <div className="hp-item" onClick={() => createSession()}>
              <div className="hp-item-icon-wrap"><FaFileExcel className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Excel Export</strong></div>
            </div>
            <div className="hp-item" onClick={() => startDatabaseSession()}>
              <div className="hp-item-icon-wrap"><FaDatabase className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Datenbank Import</strong></div>
            </div>
            <div className="hp-item hp-batch-item" onClick={() => setShowExcelBatch(true)}>
              <div className="hp-item-icon-wrap"><FaSync className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Batch CSV → UMS</strong></div>
            </div>
          </div>
        </div>

        {/* ── Posteingang ────────────────────────── */}
        {(hasModule("zuordnen") || hasModule("zaehlerstand")) && (
        <div className="hp-section hp-sec-zuordnen">
          <div className="hp-section-header hp-zuordnen">
            <div className="hp-section-icon-box"><FaLink /></div>
            <h3 className="hp-section-title">Posteingang</h3>
          </div>
          <div className="hp-items">
            {hasModule("zuordnen") && <div className="hp-item" onClick={() => navigate("/zuordnen/verschieben")}>
              <div className="hp-item-icon-wrap"><FaExchangeAlt className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Verschieben</strong></div>
            </div>}
            {hasModule("zaehlerstand") && <div className="hp-item" onClick={() => navigate("/zuordnen/bilder", { state: { autoAnalyse: true } })}>
              <div className="hp-item-icon-wrap"><FaCamera className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Zählerstände</strong></div>
            </div>}
            {hasModule("zuordnen") && <div className="hp-item" onClick={() => navigate("/zuordnen/fotos")}>
              <div className="hp-item-icon-wrap"><FaMapMarkerAlt className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Bilder verarbeiten</strong></div>
            </div>}
          </div>
        </div>
        )}

        {/* ── Mietverwaltung ────────────────────────────── */}
        {hasModule("mietverwaltung") && (
        <div className="hp-section hp-sec-miet">
          <div className="hp-section-header hp-miet">
            <div className="hp-section-icon-box"><FaHome /></div>
            <h3 className="hp-section-title">Mietverwaltung</h3>
          </div>
          <div className="hp-items">
            <div className="hp-item" onClick={() => navigate("/mietverwaltung?tab=zahlungen")}>
              <div className="hp-item-icon-wrap"><FaMoneyBillWave className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Zahlungen</strong></div>
            </div>
            <div className="hp-item" onClick={() => navigate("/mietverwaltung?tab=vertraege")}>
              <div className="hp-item-icon-wrap"><FaFileContract className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Objekte &amp; Verträge</strong></div>
            </div>
            <div className="hp-item" onClick={() => navigate("/mietverwaltung?tab=historie")}>
              <div className="hp-item-icon-wrap"><FaChartLine className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Miethistorie</strong></div>
            </div>
            <div className="hp-item" onClick={() => navigate("/mietverwaltung?tab=schaeden")}>
              <div className="hp-item-icon-wrap"><FaShieldAlt className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Schäden</strong></div>
            </div>
          </div>
        </div>
        )}

        {/* ── Rename Me ─── */}
        {hasModule("rename") && (
        <div className="hp-section hp-sec-rename">
          <div className="hp-section-header hp-rename">
            <div className="hp-section-icon-box"><FaEdit /></div>
            <h3 className="hp-section-title">Rename Me</h3>
          </div>
          <div className="hp-items">
            <div className="hp-item" onClick={() => navigate("/rename")}>
              <div className="hp-item-icon-wrap"><FaFileAlt className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Einzeldatei umbenennen</strong></div>
            </div>
            <div className="hp-item" onClick={() => navigate("/rename-engine")}>
              <div className="hp-item-icon-wrap"><FaFileAlt className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Rename-Engine 2.0 (neu)</strong></div>
            </div>
            <div className="hp-item" onClick={() => { setShowBatchModal(true); setReviewTrigger(t => t + 1); }}>
              <div className="hp-item-icon-wrap"><FaClipboardList className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Review</strong></div>
            </div>
            <div
              className={`hp-item hp-batch-item${runningBatch ? " hp-item-disabled" : ""}`}
              onClick={!runningBatch ? handleBatchRename : undefined}
              title={runningBatch ? "Batch läuft bereits…" : "Alle Dateien in der Inbox verarbeiten"}
            >
              <div className="hp-item-icon-wrap"><FaSync className={`hp-item-icon${runningBatch ? " spin" : ""}`} /></div>
              <div className="hp-item-text"><strong>{runningBatch ? "Batch läuft…" : "Batch starten"}</strong></div>
            </div>
          </div>
          {batchError && !showBatchModal && (
            <p className="hp-batch-error">{batchError}</p>
          )}
        </div>
        )}

        {/* ── Tools ─────────────────────────────────── */}
        {hasModule("zuordnen") && (
        <div className="hp-section hp-sec-analysen">
          <div className="hp-section-header hp-analysen">
            <div className="hp-section-icon-box"><FaSearchPlus /></div>
            <h3 className="hp-section-title">Tools</h3>
          </div>
          <div className="hp-items">
            <div className="hp-item" onClick={() => setDataPathPickerOpen(true)}>
              <div className="hp-item-icon-wrap"><FaBuilding className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Firmen &amp; Objekte</strong></div>
            </div>
            <div className="hp-item" onClick={() => navigate("/tools/komprimieren")}>
              <div className="hp-item-icon-wrap"><FaFileArchive className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Datei komprimieren</strong></div>
            </div>
            <div className="hp-item" onClick={() => navigate("/tools/vb-vergleich")}>
              <div className="hp-item-icon-wrap"><FaExchangeAlt className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>PDF Vergleich</strong></div>
            </div>
            {user?.rolle === "admin" && (
              <div className="hp-item" onClick={() => navigate("/admin-tools")}>
                <div className="hp-item-icon-wrap"><FaUsers className="hp-item-icon" /></div>
                <div className="hp-item-text"><strong>Tool-Portal Benutzer</strong></div>
              </div>
            )}
            <div className="hp-item" onClick={() => navigate("/zuordnen/komprimierung")}>
              <div className="hp-item-icon-wrap"><FaTachometerAlt className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Zähler komprimieren</strong></div>
            </div>
            <div className="hp-item" onClick={() => navigate("/zuordnen/bilder-komprimierung")}>
              <div className="hp-item-icon-wrap"><FaImage className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Bilder komprimieren</strong></div>
            </div>
          </div>
        </div>
        )}

        {/* ── Steuer & Rechnungen ─────────────────────────── */}
        {(hasModule("rechnungen") || hasModule("fahrtenbuch") || hasModule("arbeitsnachweis")) && (
        <div className="hp-section hp-sec-steuer">
          <div className="hp-section-header hp-steuer">
            <div className="hp-section-icon-box"><FaFileInvoiceDollar /></div>
            <h3 className="hp-section-title">Steuer & Rechnungen</h3>
          </div>
          <div className="hp-items">
            {hasModule("rechnungen") && <div className="hp-item" onClick={() => navigate("/rechnungen")}>
              <div className="hp-item-icon-wrap"><FaFileInvoiceDollar className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Rechnungen</strong></div>
            </div>}
            {hasModule("rechnungen") && <div className="hp-item" onClick={() => navigate("/datev")}>
              <div className="hp-item-icon-wrap"><FaExchangeAlt className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>DATEV-Versand</strong></div>
            </div>}
            {hasModule("fahrtenbuch") && <div className="hp-item" onClick={() => navigate("/fahrtenbuch")}>
              <div className="hp-item-icon-wrap"><FaCar className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Fahrtenbuch</strong></div>
            </div>}
            {hasModule("arbeitsnachweis") && <div className="hp-item" onClick={() => navigate("/arbeitsnachweis")}>
              <div className="hp-item-icon-wrap"><FaClipboardList className="hp-item-icon" /></div>
              <div className="hp-item-text"><strong>Arbeitsnachweis</strong></div>
            </div>}
          </div>
        </div>
        )}
      </div>
      )}

      {showBatchModal && (
        <div className="modal-overlay">
          <div className={`modal-card ${reviewMode ? 'review-mode' : ''}`} role="dialog" aria-modal="true">
            
            {/* === PHASE 1: BATCH LÄUFT === */}
            {!reviewMode && (
              <>
                <h3 className="modal-title">{batchDone ? 'Batch abgeschlossen' : 'Batch läuft …'}</h3>
                <p className="modal-sub">
                  {batchDone 
                    ? (folderStats.error > 0 
                        ? `${folderStats.error} Datei(en) mit Fehlern gefunden – Review wird geladen...` 
                        : 'Alle Dateien wurden erfolgreich verarbeitet!')
                    : 'Batch läuft – hol dir einen Tee ☕️, wir sagen Bescheid, wenn es durch ist.'}
                </p>

                <div className={`progressbar ${batchDone ? 'done' : ''}`}>
                  <div className="progressbar-fill" style={{ width: `${progressPct}%` }} />
                </div>

                <div className="stats-grid">
                  <div className="stat-box"><span>Inbox</span><strong>{folderStats.inbox ?? '–'}</strong></div>
                  <div className="stat-box"><span>Outbox</span><strong>{folderStats.outbox ?? '–'}</strong></div>
                  <div className="stat-box"><span>Archiv</span><strong>{folderStats.processed ?? '–'}</strong></div>
                  <div className={`stat-box ${folderStats.error > 0 ? 'has-errors' : ''}`}><span>Error</span><strong>{folderStats.error ?? '–'}</strong></div>
                </div>

                <div className="feed-box">
                  <div className="feed-head">Live-Feed</div>
                  <ul className="feed-list" ref={feedListRef}>
                    {liveFeed.map((it, idx) => {
                      const isKi = it.status === "spinner" && String(it.text).includes("KI läuft");
                      const isSpinner = it.status === "spinner";
                      const isOk = it.status === "ok";
                      const isError = it.status === "error";

                      // Strukturierte Batch-Datei-Einträge
                      if (it.baseName) {
                        const icon = isSpinner ? (isKi ? "⏳" : "⏳") : isOk ? "✓" : "✗";
                        const iconColor = isSpinner ? "#6b7280" : isOk ? "#22c55e" : "#ef4444";
                        return (
                          <li key={it.id || idx} className={`feed-item feed-item--${it.status}`}>
                            <span className="feed-icon" style={{ color: iconColor }}>{icon}</span>
                            <span className="feed-ts">{it.ts}</span>
                            <span className="feed-names">
                              <span className="feed-orig">{it.baseName}</span>
                              {isSpinner ? (
                                <span className="feed-phase" style={{ color: isKi ? "#f59e0b" : "#6b7280" }}>
                                  {String(it.text)}
                                </span>
                              ) : isOk && it.finalName ? (
                                <span className="feed-arrow">→ <span className="feed-final">{it.finalName}</span></span>
                              ) : isError ? (
                                <span className="feed-error-msg">{it.message || it.finalName || "Fehler"}</span>
                              ) : null}
                            </span>
                            {it.durLabel && !isSpinner && (
                              <span className="feed-dur">{it.durLabel}</span>
                            )}
                          </li>
                        );
                      }

                      // Einfache Text-Einträge (z.B. Info-Zeilen)
                      const icon = isSpinner ? "○" : isOk ? "✓" : "✗";
                      return (
                        <li key={it.id || idx} style={isKi ? { color: "#f59e0b", fontWeight: 600 } : {}}>
                          <span style={{ marginRight: 8 }}>{icon}</span>
                          {String(it.text)}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {batchDone && folderStats.error === 0 && (
                  <div className="done-box">
                    <div className="tick" aria-hidden>✓</div>
                    <div className="done-text">Fertig! Alle Dateien wurden verarbeitet.</div>
                  </div>
                )}

                <div className="modal-actions">
                  <button className="home-button" onClick={closeBatchModal} disabled={runningBatch}>
                    {runningBatch ? 'Bitte warten…' : 'Schließen'}
                  </button>
                </div>
              </>
            )}

            {/* === PHASE 2: REVIEW MODUS === */}
            {reviewMode && (
              <>
                {reviewLoading ? (
                  <div className="review-loading">
                    <div className="progressbar">
                      <div className="progressbar-fill" />
                    </div>
                    <span>Lade Dateien aus Dropbox...</span>
                  </div>
                ) : reviewApplyResult?.ok ? (
                  <div className="done-box-container">
                    <div className="done-box">
                      <div className="tick" aria-hidden>✓</div>
                      <div className="done-text">
                        Fertig! {reviewApplyResult.moved_count} Datei(en) verschoben.
                      </div>
                    </div>
                    <div className="review-actions">
                      <button className="home-button" onClick={closeBatchModal}>
                        Schließen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="review-split-view">
                    {/* Linke Seite: Header, Dateiliste, Buttons unten */}
                    <div className="review-left-panel">
                      <div className="review-header">
                        <h3 className="modal-title">📝 Review</h3>
                        <p className="modal-sub">
                          {reviewErrorFiles.length > 0 && <span className="stat-error">{reviewErrorFiles.length} Fehler</span>}
                          {reviewWarningFiles.length > 0 && <span className="stat-warning">{reviewWarningFiles.length} Warnung</span>}
                          {reviewSuccessFiles.length > 0 && <span className="stat-success">{reviewSuccessFiles.length} OK</span>}
                          {pendingReprocessIds.size > 0 && <span className="stat-pending">{pendingReprocessIds.size} Bearbeitet</span>}
                        </p>
                      </div>

                      <div className="review-file-list">
                        {/* Fehler-Dateien */}
                        {reviewErrorFiles.length > 0 && (
                          <div className="review-section">
                            <div 
                              className="review-section-header error collapsible"
                              onClick={() => toggleReviewSection('fehler')}
                            >
                              {reviewCollapsed.fehler ? <FaChevronRight /> : <FaChevronDown />}
                              <FaExclamationTriangle />
                              <span>Fehler ({reviewErrorFiles.length})</span>
                            </div>
                            {!reviewCollapsed.fehler && (
                            <div className="review-files">
                              {reviewErrorFiles.map(file => {
                                const isPending = pendingReprocessIds.has(file.id);
                                return (
                                <div 
                                  key={file.id}
                                  className={`review-file-row ${reviewSelectedFileId === file.id ? 'selected' : ''} ${isPending ? 'pending' : ''}`}
                                  onClick={() => handleReviewFileSelect(file)}
                                >
                                  {isPending ? (
                                    <span className="file-icon pending" title="Bereit für neuen Batch">✏️</span>
                                  ) : (
                                    <FaExclamationTriangle className="file-icon error" />
                                  )}
                                  <div className="file-info">
                                    <div className="file-original">{file.original_name}</div>
                                    {reviewEditingId === file.id ? (
                                      <div className="file-edit">
                                        <input
                                          ref={reviewInputRef}
                                          type="text"
                                          value={reviewEditValue}
                                          onChange={(e) => handleReviewNameChange(e.target.value)}
                                          onBlur={() => {
                                            if (!reviewEditCancelledRef.current) {
                                              saveReviewFilename(reviewEditingId, reviewEditValue);
                                            }
                                            reviewEditCancelledRef.current = false;
                                            stopReviewEdit();
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              reviewInputRef.current?.blur();
                                            } else if (e.key === "Escape") {
                                              reviewEditCancelledRef.current = true;
                                              reviewInputRef.current?.blur();
                                            }
                                          }}
                                          autoFocus
                                        />
                                        <span>.pdf</span>
                                        {reviewSaving && <FaSpinner className="spin" />}
                                      </div>
                                    ) : (
                                      <div 
                                        className="file-proposed"
                                        onClick={(e) => { e.stopPropagation(); startReviewEdit(file.id, file.proposed_name); }}
                                      >
                                        {file.proposed_name}
                                        <FaEdit className="edit-icon" />
                                      </div>
                                    )}
                                  </div>
                                  {!isPending && (
                                    <div className="file-actions">
                                      <button 
                                        className="mark-reprocess-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPendingReprocessIds(prev => new Set([...prev, file.id]));
                                        }}
                                        title="Für Reprocess markieren"
                                      >
                                        ↻
                                      </button>
                                      <button 
                                        className="add-entry-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openAddEntryModal(file);
                                        }}
                                        title="Eintrag in DB hinzufügen"
                                      >
                                        <FaPlus className="btn-icon-plus" />
                                        <FaDatabase className="btn-icon-db" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )})}
                            </div>
                            )}
                          </div>
                        )}

                        {/* Warnung-Dateien - haben # im Namen */}
                        {reviewWarningFiles.length > 0 && (
                          <div className="review-section">
                            <div 
                              className="review-section-header warning collapsible"
                              onClick={() => toggleReviewSection('warnung')}
                            >
                              {reviewCollapsed.warnung ? <FaChevronRight /> : <FaChevronDown />}
                              <FaExclamationTriangle />
                              <span>Warnung ({reviewWarningFiles.length})</span>
                            </div>
                            {!reviewCollapsed.warnung && (
                            <div className="review-files">
                              {reviewWarningFiles.map(file => {
                                const isPending = pendingReprocessIds.has(file.id);
                                return (
                                <div 
                                  key={file.id}
                                  className={`review-file-row warning ${reviewSelectedFileId === file.id ? 'selected' : ''} ${isPending ? 'pending' : ''}`}
                                  onClick={() => handleReviewFileSelect(file)}
                                >
                                  {isPending ? (
                                    <span className="file-icon pending" title="Bereit für neuen Batch">✏️</span>
                                  ) : (
                                    <FaExclamationTriangle className="file-icon warning" />
                                  )}
                                  <div className="file-info">
                                    <div className="file-original">{file.original_name}</div>
                                    {reviewEditingId === file.id ? (
                                      <div className="file-edit">
                                        <input
                                          ref={reviewInputRef}
                                          type="text"
                                          value={reviewEditValue}
                                          onChange={(e) => handleReviewNameChange(e.target.value)}
                                          onBlur={() => {
                                            if (!reviewEditCancelledRef.current) {
                                              saveReviewFilename(reviewEditingId, reviewEditValue);
                                            }
                                            reviewEditCancelledRef.current = false;
                                            stopReviewEdit();
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              reviewInputRef.current?.blur();
                                            } else if (e.key === "Escape") {
                                              reviewEditCancelledRef.current = true;
                                              reviewInputRef.current?.blur();
                                            }
                                          }}
                                          autoFocus
                                        />
                                        <span>.pdf</span>
                                        {reviewSaving && <FaSpinner className="spin" />}
                                      </div>
                                    ) : (
                                      <div 
                                        className="file-proposed"
                                        onClick={(e) => { e.stopPropagation(); startReviewEdit(file.id, file.proposed_name); }}
                                      >
                                        {file.proposed_name}
                                        <FaEdit className="edit-icon" />
                                      </div>
                                    )}
                                  </div>
                                  {!isPending && (
                                    <div className="file-actions">
                                      <button 
                                        className="mark-reprocess-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPendingReprocessIds(prev => new Set([...prev, file.id]));
                                        }}
                                        title="Für Reprocess markieren"
                                      >
                                        ↻
                                      </button>
                                      <button 
                                        className="add-entry-btn warning"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openAddEntryModal(file);
                                        }}
                                        title="Eintrag in DB hinzufügen"
                                      >
                                        <FaPlus className="btn-icon-plus" />
                                        <FaDatabase className="btn-icon-db" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )})}
                            </div>
                            )}
                          </div>
                        )}

                        {/* Bearbeitete Dateien - aus Error-Ordner, jetzt OK */}
                        {reviewEditedFiles.length > 0 && (
                          <div className="review-section">
                            <div className="review-section-header edited">
                              <FaEdit />
                              <span>Bearbeitet ({reviewEditedFiles.length})</span>
                            </div>
                            <div className="review-files">
                              {reviewEditedFiles.map(file => (
                                <div 
                                  key={file.id}
                                  className={`review-file-row edited ${reviewSelectedFileId === file.id ? 'selected' : ''}`}
                                  onClick={() => handleReviewFileSelect(file)}
                                >
                                  <FaEdit className="file-icon edited" />
                                  <div className="file-info">
                                    <div className="file-original">{file.original_name}</div>
                                    {reviewEditingId === file.id ? (
                                      <div className="file-edit">
                                        <input
                                          ref={reviewInputRef}
                                          type="text"
                                          value={reviewEditValue}
                                          onChange={(e) => handleReviewNameChange(e.target.value)}
                                          onBlur={() => {
                                            if (!reviewEditCancelledRef.current) {
                                              saveReviewFilename(reviewEditingId, reviewEditValue);
                                            }
                                            reviewEditCancelledRef.current = false;
                                            stopReviewEdit();
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              reviewInputRef.current?.blur();
                                            } else if (e.key === "Escape") {
                                              reviewEditCancelledRef.current = true;
                                              reviewInputRef.current?.blur();
                                            }
                                          }}
                                          autoFocus
                                        />
                                        <span>.pdf</span>
                                        {reviewSaving && <FaSpinner className="spin" />}
                                      </div>
                                    ) : (
                                      <div 
                                        className="file-proposed"
                                        onClick={(e) => { e.stopPropagation(); startReviewEdit(file.id, file.proposed_name); }}
                                      >
                                        {file.proposed_name}
                                        <FaEdit className="edit-icon" />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Erfolgreiche Dateien (Dropdown) */}
                        {reviewSuccessFiles.length > 0 && (
                          <div className="review-section">
                            <div 
                              className="review-section-header success collapsible"
                              onClick={() => setReviewShowSuccess(!reviewShowSuccess)}
                            >
                              {reviewShowSuccess ? <FaChevronDown /> : <FaChevronRight />}
                              <FaCheckCircle />
                              <span>OK ({reviewSuccessFiles.length})</span>
                            </div>
                            {reviewShowSuccess && (
                              <div className="review-files">
                                {reviewSuccessFiles.map(file => (
                                  <div 
                                    key={file.id}
                                    className={`review-file-row success ${reviewSelectedFileId === file.id ? 'selected' : ''}`}
                                    onClick={() => handleReviewFileSelect(file)}
                                  >
                                    <FaCheckCircle className="file-icon success" />
                                    <div className="file-info">
                                      <div className="file-original">{file.original_name}</div>
                                      {reviewEditingId === file.id ? (
                                        <div className="file-edit">
                                          <input
                                            ref={reviewInputRef}
                                            type="text"
                                            value={reviewEditValue}
                                            onChange={(e) => handleReviewNameChange(e.target.value)}
                                            onBlur={() => {
                                              if (!reviewEditCancelledRef.current) {
                                                saveReviewFilename(reviewEditingId, reviewEditValue);
                                              }
                                              reviewEditCancelledRef.current = false;
                                              stopReviewEdit();
                                            }}
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter") {
                                                reviewInputRef.current?.blur();
                                              } else if (e.key === "Escape") {
                                                reviewEditCancelledRef.current = true;
                                                reviewInputRef.current?.blur();
                                              }
                                            }}
                                            autoFocus
                                          />
                                          <span>.pdf</span>
                                          {reviewSaving && <FaSpinner className="spin" />}
                                        </div>
                                      ) : (
                                        <div 
                                          className="file-proposed"
                                          onClick={(e) => { e.stopPropagation(); startReviewEdit(file.id, file.proposed_name); }}
                                        >
                                          {file.proposed_name}
                                          <FaEdit className="edit-icon" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="file-actions">
                                      <button 
                                        className="mark-reprocess-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPendingReprocessIds(prev => new Set([...prev, file.id]));
                                        }}
                                        title="Für Reprocess markieren"
                                      >
                                        ↻
                                      </button>
                                      <button 
                                        className="add-entry-btn success"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openAddEntryModal(file);
                                        }}
                                        title="Eintrag in DB hinzufügen"
                                      >
                                        <FaPlus className="btn-icon-plus" />
                                        <FaDatabase className="btn-icon-db" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Buttons unten */}
                      <div className="review-actions">
                        {pendingReprocessIds.size > 0 && (
                          <button 
                            className="home-button reprocess-btn"
                            onClick={handleReprocessPending}
                            disabled={reprocessing}
                            title="Bearbeitete Dateien nochmal durch den Batch schicken"
                          >
                            {reprocessing ? <><FaSpinner className="spin" /> Verarbeite...</> : <><FaSync /> {pendingReprocessIds.size} neu verarbeiten</>}
                          </button>
                        )}
                        {!reviewApplyResult?.ok && reviewEditedFiles.length > 0 && (
                          <button 
                            className="home-button primary"
                            onClick={handleReviewApplyAll}
                            disabled={reviewApplying}
                          >
                            {reviewApplying ? <><FaSpinner className="spin" /> Verschiebe...</> : `${reviewEditedFiles.length} → Outbox`}
                          </button>
                        )}
                        {!reviewApplyResult?.ok && reviewEditedFiles.length === 0 && reviewErrorFiles.length > 0 && pendingReprocessIds.size === 0 && (
                          <span className="review-hint">{reviewErrorFiles.length} Datei(en) haben noch Fehler</span>
                        )}
                        <button className="home-button" onClick={closeBatchModal}>
                          Schließen
                        </button>
                      </div>
                    </div>

                    {/* Rechte Seite: PDF Preview - volle Höhe */}
                    <div className="review-preview">
                      {reviewSelectedFile && (
                        <div className="preview-header">
                          <strong>{reviewSelectedFile.original_name}</strong>
                          <span className={`route-badge ${reviewSelectedFile.route?.toLowerCase()}`}>
                            {reviewSelectedFile.route}
                          </span>
                        </div>
                      )}
                      <div className="preview-content">
                        {reviewPreviewUrl ? (
                          <img 
                            src={reviewPreviewUrl} 
                            alt="PDF Preview" 
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        ) : (
                          <div className="preview-placeholder">
                            <FaFileAlt />
                            <span>Wähle eine Datei</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Excel Batch Dropbox Modal */}
      <ExcelBatchPage 
        isOpen={showExcelBatch} 
        onClose={() => setShowExcelBatch(false)} 
      />

      {/* Add Entry Modal */}
      <AddEntryModal
        isOpen={addEntryModalOpen}
        onClose={() => setAddEntryModalOpen(false)}
        onSaved={handleEntrySaved}
        defaultTable={addEntryTarget?.defaultTable}
        prefillData={addEntryTarget?.prefillData || {}}
        errorTag={addEntryTarget?.errorTag}
      />

      {/* Toast Notification */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
        duration={3000}
      />
      {dataPathPickerOpen && (
        <div className="hp-dp-overlay" onClick={() => setDataPathPickerOpen(false)}>
          <div className="hp-dp-modal" onClick={e => e.stopPropagation()}>
            <h3>Datenpfad wählen</h3>
            <p>Welchen Pfad möchtest du verarbeiten?</p>
            <div className="hp-dp-buttons">
              <button onClick={() => { setDataPathPickerOpen(false); navigate("/zuordnen?root=firmen"); }}>
                🏢 Firmen
              </button>
              <button onClick={() => { setDataPathPickerOpen(false); navigate("/zuordnen?root=objekte"); }}>
                🏘️ Objekte
              </button>
            </div>
            <button className="hp-dp-cancel" onClick={() => setDataPathPickerOpen(false)}>Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;